import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FilePlus2, KeyRound, Lightbulb, Newspaper, Settings2, ShieldCheck } from 'lucide-react';
import { Banner, BrandIcon, Button, Card, Checkbox, Dialog, EmptyState, Fact, Facts, Field, Input, LanguageName, Spinner, StatusPill, SubTabs } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { ContentItemDrawer } from '@/components/content/ContentItemDrawer';
import { ContentProfileDrawer } from '@/components/content/ContentProfileDrawer';
import { useRoles } from '@/hooks/useAuth';
import { useAddIdea, useCheckContentAccess, useRequestIdeas, useSiteContent } from '@/hooks/useContent';
import { toast } from '@/store/toastStore';
import { CONTENT_MODE_LABELS, CONTENT_ORIGIN_LABELS, CONTENT_STAGE_LABELS, CONTENT_STATE, formatUsd } from '@/utils/content';
import { errorText } from '@/utils/errors';
import { formatRelative } from '@/utils/format';
import { can } from '@/utils/permissions';
import type { ContentItemView, ContentState, SiteDetail } from '@/types/api';

type Filter = 'all' | 'idea' | 'work' | 'ready' | 'published' | 'failed';

const FILTER_STATES: Record<Filter, readonly ContentState[] | null> = {
  all: null,
  idea: ['idea'],
  work: ['queued', 'writing', 'publishing'],
  ready: ['ready'],
  published: ['published'],
  failed: ['failed', 'rejected'],
};

function AddIdeaDialog({ siteId, onClose }: { readonly siteId: string; readonly onClose: () => void }): ReactNode {
  const [title, setTitle] = useState('');
  const [keyword, setKeyword] = useState('');
  const [brief, setBrief] = useState('');
  const [write, setWrite] = useState(true);
  const add = useAddIdea(siteId, {
    onSuccess: () => {
      toast.success(write ? 'Статья поставлена в очередь' : 'Тема добавлена');
      onClose();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    add.mutate({ title: title.trim(), keyword: keyword.trim() || null, brief: brief.trim() || null, write });
  };
  return (
    <Dialog
      open
      title="Своя тема"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="content-idea" busy={add.isPending} disabled={title.trim().length < 5}>
            Добавить
          </Button>
        </>
      }
    >
      <form id="content-idea" className="stack" onSubmit={submit}>
        <Field label="Заголовок или тема" htmlFor="idea-new-title" {...(add.isError ? { error: errorText(add.error) } : {})}>
          <Input id="idea-new-title" value={title} onChange={(event) => setTitle(event.target.value)} autoFocus />
        </Field>
        <Field label="Ключевое слово" htmlFor="idea-new-keyword" hint="Необязательно">
          <Input id="idea-new-keyword" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </Field>
        <Field label="О чём статья" htmlFor="idea-new-brief" hint="Угол, тезисы, что обязательно упомянуть">
          <textarea id="idea-new-brief" className="input" rows={4} value={brief} onChange={(event) => setBrief(event.target.value)} />
        </Field>
        <Checkbox checked={write} onChange={setWrite} label="Сразу написать" />
      </form>
    </Dialog>
  );
}

function ItemsTable({ items, onOpen }: { readonly items: readonly ContentItemView[]; readonly onOpen: (id: string) => void }): ReactNode {
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th scope="col">Статья</th>
            <th scope="col">Состояние</th>
            <th scope="col">Откуда</th>
            <th scope="col" className="numeric">Стоимость</th>
            <th scope="col">Изменена</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="row-link" onClick={() => onOpen(item.id)}>
              <td>
                <button type="button" className="link-button" onClick={() => onOpen(item.id)}>
                  {item.title}
                </button>
                {item.keyword ? <div className="muted">{item.keyword}</div> : null}
              </td>
              <td>
                <StatusPill map={CONTENT_STATE} status={item.state} />
                {item.state === 'writing' && item.stage ? <div className="muted text-sm">{CONTENT_STAGE_LABELS[item.stage]}</div> : null}
              </td>
              <td className="muted">{CONTENT_ORIGIN_LABELS[item.origin] ?? item.origin}</td>
              <td className="numeric">{item.costUsd !== null ? formatUsd(item.costUsd) : '—'}</td>
              <td className="muted">{formatRelative(item.publishedAt ?? item.updatedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A site's articles (ADR-0051): what they are about and how they go out, the
 * topics proposed and typed in, and each article from idea to published.
 */
export function ContentTab({ site }: { readonly site: SiteDetail }): ReactNode {
  const roles = useRoles();
  const content = useSiteContent(site.id);
  const [profileOpen, setProfileOpen] = useState(false);
  const [ideaOpen, setIdeaOpen] = useState(false);
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const check = useCheckContentAccess(site.id);
  const ideas = useRequestIdeas(site.id, {
    onSuccess: () => toast.success('Темы появятся через минуту-две'),
    onError: (error) => toast.error('Не удалось запросить темы', errorText(error)),
  });
  const mayManage = can(roles, 'content.manage');

  if (content.isPending) return <Spinner />;
  if (content.isError) return <ErrorState error={content.error} />;
  const view = content.data;
  const profile = view.profile;
  const shown = view.items.filter((item) => FILTER_STATES[filter]?.includes(item.state) ?? true);
  const counted = (states: readonly ContentState[] | null) => (states ? states.reduce((sum, state) => sum + (view.counts[state] ?? 0), 0) : view.items.length);

  return (
    <div className="stack">
      {!view.available ? (
        <Banner tone="warning" icon={KeyRound}>
          Ключ Claude не задан. {can(roles, 'secrets.manage') ? <Link to="/integrations/anthropic">Подключить</Link> : 'Его задаёт владелец или администратор.'}
        </Banner>
      ) : null}

      <Card
        title="Профиль"
        icon={Newspaper}
        actions={
          mayManage ? (
            <Button size="sm" icon={Settings2} onClick={() => setProfileOpen(true)}>
              {profile ? 'Изменить' : 'Настроить'}
            </Button>
          ) : null
        }
      >
        {profile ? (
          <div className="stack">
            <Facts>
              <Fact label="Тематика">{profile.topic}</Fact>
              <Fact label="Язык">{view.language ? <LanguageName language={view.language} /> : <span className="tone-down">не задан</span>}</Fact>
              <Fact label="Режим">{CONTENT_MODE_LABELS[profile.mode]}</Fact>
              <Fact label="Автопилот">{profile.enabled ? `включён · ${profile.perWeek} в неделю` : 'выключен'}</Fact>
              <Fact label="WordPress" icon={<BrandIcon name="wordpress" size={14} />}>
                {profile.credentialId ? 'доступ задан' : <span className="faint">доступ не задан — статьи только в панели</span>}
                {check.data ? (
                  check.data.ok ? (
                    <span className="tone-healthy"> · публикация доступна ({check.data.user})</span>
                  ) : (
                    <span className="tone-down"> · {check.data.error}</span>
                  )
                ) : null}
              </Fact>
              <Fact label="Расход за месяц">{formatUsd(view.monthUsd)}</Fact>
            </Facts>
            {mayManage && profile.credentialId ? (
              <div>
                <Button size="sm" icon={ShieldCheck} busy={check.isPending} onClick={() => check.mutate()}>
                  Проверить доступ
                </Button>
              </div>
            ) : null}
          </div>
        ) : (
          <EmptyState icon={Newspaper} message="Профиль не настроен: без него темы и статьи не пишутся" />
        )}
      </Card>

      <Card
        flush
        title="Статьи"
        icon={FilePlus2}
        actions={
          mayManage && profile ? (
            <div className="row-tight">
              <Button size="sm" icon={Lightbulb} busy={ideas.isPending} disabled={!view.available || view.ideasRequested > 0} onClick={() => ideas.mutate(5)}>
                {view.ideasRequested > 0 ? 'Темы готовятся' : 'Предложить темы'}
              </Button>
              <Button size="sm" icon={FilePlus2} onClick={() => setIdeaOpen(true)}>
                Своя тема
              </Button>
            </div>
          ) : null
        }
      >
        {view.ideasRequested > 0 ? (
          <div className="card-pad">
            <Banner icon={Lightbulb} spin>
              Claude подбирает {view.ideasRequested} {view.ideasRequested === 1 ? 'тему' : view.ideasRequested < 5 ? 'темы' : 'тем'} · обычно до минуты
            </Banner>
          </div>
        ) : null}
        <div className="card-pad">
          <SubTabs
            prefix="content"
            label="Статьи по состоянию"
            active={filter}
            onChange={setFilter}
            tabs={[
              { id: 'all', label: 'Все', count: counted(null) },
              { id: 'idea', label: 'Темы', count: counted(FILTER_STATES.idea) },
              { id: 'work', label: 'В работе', count: counted(FILTER_STATES.work) },
              { id: 'ready', label: 'Готовы', count: counted(FILTER_STATES.ready) },
              { id: 'published', label: 'В CMS', count: counted(FILTER_STATES.published) },
              { id: 'failed', label: 'Ошибки', count: counted(FILTER_STATES.failed) },
            ]}
          />
        </div>
        {shown.length === 0 ? <EmptyState icon={Newspaper} message="Статей нет" /> : <ItemsTable items={shown} onOpen={setOpenItem} />}
      </Card>

      {profileOpen ? (
        <ContentProfileDrawer siteId={site.id} profile={profile} siteLanguage={site.language} mayChooseLogin={can(roles, 'secrets.manage')} onClose={() => setProfileOpen(false)} />
      ) : null}
      {ideaOpen ? <AddIdeaDialog siteId={site.id} onClose={() => setIdeaOpen(false)} /> : null}
      {openItem ? <ContentItemDrawer id={openItem} mayManage={mayManage} onClose={() => setOpenItem(null)} /> : null}
    </div>
  );
}
